import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaIncomeSourceEnum } from 'interfaces/cuffa.interface';
import { ROUTES } from 'routes/constants';

// Mapeamento mais completo das chaves da API para os valores do frontend
const backendKeyToIncomeSourceMap: Record<string, string> = {
  'prestacao_servico_externo': CuffaIncomeSourceEnum.PRESTACAO_SERVICO_EXTERNO,
  'assalariado_atividade_fora_propriedade': CuffaIncomeSourceEnum.ASSALARIADO_ATIVIDADE_FORA_PROPRIEDADE,
  'atividade_produtiva_propriedade': CuffaIncomeSourceEnum.ATIVIDADE_PRODUTIVA_PROPRIEDADE,
  'aposentadoria': CuffaIncomeSourceEnum.APOSENTADORIA,
  'pensao': CuffaIncomeSourceEnum.PENSAO,
  'recursos_programas_sociais': CuffaIncomeSourceEnum.RECURSOS_PROGRAMAS_SOCIAIS,
  'outros': 'Outro', // Valor especial para o campo "Outro"
};

interface IFontesRendaData {
  selecionadas: string[];
  ordens: { value: string; order: number }[];
  outroTexto: string;
}

export const FontesRendaFamiliaVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [dados, setDados] = useState<IFontesRendaData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIncomeSourcesData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }
      // Primeiro tente restaurar do localStorage (chave específica por uuid, depois chave global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.fontesRendaFamilia;
            if (!saved) continue;

            const selecionadas: string[] = Array.isArray(saved.selecionadas) ? saved.selecionadas : [];
            const ordens: { value: string; order: number }[] = Array.isArray(saved.ordens) ? saved.ordens : [];
            const outroTexto: string = typeof saved.outroTexto === 'string' ? saved.outroTexto : '';

            setDados({ selecionadas, ordens, outroTexto });
            setLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar fontesRendaFamilia:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar fontesRendaFamilia', e);
      }

      // Se não encontrou no storage, buscar pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        console.log('Dados recebidos da API:', response); // Log para debug

        if (response?.dados) {
          const incomeSourcesData = response.dados.income_sources || response.dados.family_income_sources || {};
          console.log('Dados de fontes de renda:', incomeSourcesData); // Log para debug

          const selecionadas: string[] = [];
          const ordens: { value: string; order: number }[] = [];
          let outroTexto = '';

          // Processa cada item retornado pela API
          Object.entries(incomeSourcesData).forEach(([backendKey, value]) => {
            const frontendValue = backendKeyToIncomeSourceMap[backendKey];

            if (frontendValue) {
              if (backendKey === 'outros' && Array.isArray(value)) {
                // Tratamento especial para o campo "Outros"
                const outroItem = value[0];
                if (outroItem) {
                  selecionadas.push(frontendValue);
                  ordens.push({ value: frontendValue, order: outroItem.priority });
                  outroTexto = outroItem.description || '';
                }
              } else if (typeof value === 'number') {
                // Para os demais campos que são números (prioridades)
                selecionadas.push(frontendValue);
                ordens.push({ value: frontendValue, order: value });
              }
            }
          });

          console.log('Dados processados:', { selecionadas, ordens, outroTexto }); // Log para debug

          setDados({ selecionadas, ordens, outroTexto });
        } else {
          console.error("Dados de fontes de renda não encontrados na resposta");
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchIncomeSourcesData();
  }, [uuid]);

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Fontes de Renda da Família" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Fontes de Renda da Família" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  const fontesOrdenadas = dados?.ordens
    ? [...dados.ordens].sort((a, b) => a.order - b.order)
    : [];

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização das Fontes de Renda da Família" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Fontes de Renda da Família" width="100%">
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Fontes de Renda (em ordem de prioridade)
              </Typography>

              {fontesOrdenadas.length > 0 ? (
                <List>
                  {fontesOrdenadas.map((fonte, index) => (
                    <div key={`${fonte.value}-${index}`}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={2}>
                              <Chip
                                label={`${fonte.order}º`}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                              <Typography>
                                {fonte.value === 'Outro' && dados?.outroTexto
                                  ? `OUTRO: ${dados.outroTexto}`
                                  : fonte.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < fontesOrdenadas.length - 1 && <Divider />}
                    </div>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma fonte de renda registrada
                </Typography>
              )}
            </Paper>

            {/* Botões de navegação */}
            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.CREDITO_FINANCIAMENTO_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => uuid && navigate(
                  ROUTES.PARTICIPACAO_FEMININA_CUFFA_VISUALIZAR.replace(':uuid', uuid)
                )}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default FontesRendaFamiliaVisualizar;

